import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatchBreakdownComponent } from '../match-breakdown.component';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatchBreakdownService } from '../match-breakdown.service';
import { defer, Observable, of, tap } from 'rxjs';
import { LetDirective } from '@ngrx/component';
import { MatchBreakdownSubmittal } from '../models/match-breakdown-submittal.model';
import { MatchBreakdownJob } from '../models/match-breakdown-job.model';
import { Store } from '@ngrx/store';
import { loadJobDetails } from 'src/app/recruiting/jobs/details/components/store/actions/job-details.actions';
import { jobDetailsLoaded } from 'src/app/recruiting/jobs/details/components/store/selectors/job-details.selectors';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatchBreakdown } from '../models/match-breakdown.model';

@UntilDestroy()
@Component({
    selector: 'nova-match-breakdown-dialog',
    templateUrl: './match-breakdown-dialog.component.html',
    styleUrls: ['./match-breakdown-dialog.component.scss'],
    standalone: true,
    imports: [
        MatIconModule,
        MatDialogModule,
        MatchBreakdownComponent,
        MatButtonModule,
        LetDirective,
        MatProgressBarModule,
        CommonModule,
        NgClass,
        NgTemplateOutlet
    ]
})
export class MatchBreakdownDialogComponent implements OnInit, OnDestroy {
    @ViewChild('content', { static: false }) content: ElementRef<HTMLElement>;

    private readonly _data = inject(MAT_DIALOG_DATA) as {
        submittal?: MatchBreakdownSubmittal;
        job?: MatchBreakdownJob;
        uiLabels: {
            jobTitle?: string;
            candidateName?: string;
            matchCategory?: string;
            hideReviewNotes?: boolean;
        };
    };

    private readonly _dialogRef = inject(MatDialogRef) as MatDialogRef<MatchBreakdownDialogComponent>;

    private readonly _matchBreakdownService = inject(MatchBreakdownService);
    private readonly _store = inject(Store);

    matchBreakdown$: Observable<MatchBreakdown>;
    continueCallBackFunc: () => void;
    isContinueEnabled = false;
    isJobScreen = false;
    jobTitle = '';
    jobDescription = '';
    isLoading = false;
    isJobDetailsOnly = false;
    candidateName: string;
    matchCategory: string;
    hideReviewNotes: boolean;

    ngOnInit(): void {
        this.candidateName = this._data?.uiLabels?.candidateName;
        this.matchCategory = this._data?.uiLabels?.matchCategory;
        this.jobTitle = this._data?.uiLabels?.jobTitle;
        this.hideReviewNotes = this._data?.uiLabels?.hideReviewNotes ?? false;

        // if submittal is not undefined/null then we are getting matching for job on submittal
        if (this._data.submittal) {
            this.isJobScreen = false;
            this.continueCallBackFunc = this._data.submittal.continueCallBackFunc;

            // if we have a submittalId then we are getting matching for the submittal
            // otherwise we are getting matching for the job (no submittal yet)
            if (this._data.submittal.submittalId) {
                this.matchBreakdown$ = this._matchBreakdownService
                    .getMatchBreakdownForSubmittal(
                        this._data.submittal.submittalId,
                        this._data.submittal.candidateUserId,
                        this._data.submittal.jobId
                    )
                    .pipe(
                        tap(() => {
                            this.onMatchBreakdownLoaded();
                        })
                    );
            } else {
                this.matchBreakdown$ = this._matchBreakdownService
                    .getMatchBreakdown(this._data.submittal.candidateUserId, +this._data.submittal.jobId)
                    .pipe(
                        tap(() => {
                            this.onMatchBreakdownLoaded();
                        })
                    );
            }
        }
        // otherwise if we have a job we are getting matching for the job (no submittal yet)
        // and we already loaded the matches so no need to query for it
        if (this._data.job) {
            this.isJobDetailsOnly = this._data.job.isJobDetailsOnly;
            this.isLoading = true;
            this._store.dispatch(loadJobDetails({ jobId: this._data.job.jobId }));

            this._store
                .select(jobDetailsLoaded)
                .pipe(untilDestroyed(this))
                .subscribe((e) => {
                    if (e.success) {
                        this.isLoading = false;
                        this.jobTitle = e.job.jobTitle;
                        this.jobDescription = e.job.description;
                    }
                });

            this.isJobScreen = true;
            this.matchBreakdown$ = defer(() => of(this._data.job.matchBreakdown));
        }
    }

    onScroll(): void {
        const element = this.content.nativeElement;
        /*
        scrollTop: The number of pixels that the content of an element is scrolled vertically.

        clientHeight: The height of the visible part of an element.

        scrollHeight: The total height of the content inside an element, including content not visible on the screen due to overflow.

        */
        const atBottom = Math.ceil(element.scrollTop) + element.clientHeight >= element.scrollHeight - 2;

        if (atBottom) {
            this.isContinueEnabled = true;
        }
    }

    close() {
        this._dialogRef.close();
    }

    onMatchBreakdownLoaded() {
        if (this.continueCallBackFunc) {
            setTimeout(() => {
                this.checkPanelHeight();
            }, 0);
        }
    }

    continue() {
        this._dialogRef.close();
        if (this.continueCallBackFunc) {
            this.continueCallBackFunc();
        }
    }

    checkPanelHeight() {
        const contentElement = this?.content?.nativeElement;
        if (contentElement?.scrollHeight > contentElement?.clientHeight) {
            contentElement?.addEventListener('scroll', this.onScroll.bind(this));
        } else if (this._data.submittal) {
            this.isContinueEnabled = true;
        }
    }

    ngOnDestroy() {
        const contentElement = this?.content?.nativeElement;
        contentElement?.removeEventListener('scroll', this.onScroll.bind(this));
    }
}
